// ============================================================
// AddActivityModal.tsx
// "Add activity" popup for client-detail mode. Mirrors
// ClientDocumentUploadModal's chrome + focus/escape/backdrop
// mechanics so all Casevo modals feel identical. The modal
// owns the CREATE flow directly — it validates locally, calls
// createActivityApi, and on success invokes onCreated() (which
// the parent uses to refetch the client's activities) then
// closes.
//
// Form fields:
//   - Action (text, required)
//   - Content (textarea, optional)
//   - Time in units (whole 6-minute units, ROUNDED UP — the
//     server charges units × rate ÷ 10, backend lib/billing.ts).
//     The timer's exact seconds are stored; a manual units
//     override is stored as units × 360s (charged as exactly
//     those units).
//   - Case (optional <select>): "No specific case" or one of
//     the client's cases. Passed through as caseId on the
//     payload; '' becomes null.
//
// The clientId + snapshot fields (actor triple, gradeAtEntry,
// hourlyRatePenceAtEntry, amountPence) are stamped server-side
// at create time. The frontend NEVER sends them.
//
// FRAME (approved mockup casevo-email-activity-modals.html, Option A —
// the same frame as ComposeEmailModal + ActivityViewModal): ModalFrame xl +
// scrollBody. Hero band = type pill + case ref + title + subtitle; body =
// the call strip (call types), the fields card (Action / With / Case) and
// the plain-text Note; footer = the compact TimerControl + Cancel / Save.
// Esc / backdrop / ✕ / focus-trap / focus-restore are ModalFrame's; every
// close routes through handleAttemptClose (the timer leave-guard). The TYPE
// is chosen BEFORE this form — by the Log-time popover (ActivityTypePicker)
// which opens it with `variant`; a variant-less open shows the light
// compact picker card first. Timer / validation / save logic unchanged.
// ============================================================
import { registerTimerNavGuard } from '../../utils/timerNavGuard'
import TimerLeaveDialog from './TimerLeaveDialog'
import { NonBillableConfirm, NotBillableNote, isChargeableWithoutCase } from './NotBillable'
import { useLeavePageWarning } from '../../hooks/useLeavePageWarning'
import { useDraftDirty } from '../../hooks/useUnsavedChangesGuard'
import React, { useEffect, useRef, useState } from 'react'
import { billableUnits, unitsToSeconds } from '../../utils/billingUnits'
import { formatDateUK } from '../../utils/caseHelpers'
import { createActivityApi, updateActivityApi } from '../../api/casevoApi'
import type { Activity } from '../../types/activity.types'
import { ACTION_TYPE_BY_VALUE } from '../../constants/actionTypes.constants'
import { TimerControl } from './emailClientLayout'
import { WhatsAppGlyph } from './PhoneLink'
import ModalFrame, { HERO_PILL_SOFT } from './ModalFrame'
import { BTN_PRIMARY } from '../../styles/buttons'
import { entryTypeFor } from '../../constants/entryTypes'
import { EntryIconSvg, UiIconSvg } from '../clients/activityIcons'
import { caseShortId } from '../../utils/caseName'
import { telHrefOf, whatsAppHrefOf } from '../../utils/phoneLinks'
import { ActivityTypePickerCard } from './ActivityTypePicker'


// ── ACTION VARIANTS ──────────────────────────────────────────
// Optional presets that specialise the CREATE flow for common
// activity kinds (call, take-call, memo, attendance, …). The
// generic "+ New activity" path is untouched: when no variant
// is passed, every field, placeholder, and payload byte matches
// the pre-variant behaviour. When a variant IS passed, the
// modal (a) retitles the header for the specific action, (b)
// swaps the Action + Note placeholders to variant-specific
// hints, (c) stamps the payload's actionType, and (d) can render
// a small context strip above Action (see clientPhone prop for
// the 'call' variant's number lookup).
//
// Shape is deliberately general so Stage 3 can drop in take-call
// / memo / attendance without touching consumers — they just add
// a member to this record and the toolbar / opener chooses which
// key to pass. Exported so ClientSearchView (and the future
// toolbar) can import ACTION_VARIANTS[key].title() etc.
export type ActionVariantKey = 'call' | 'takeCall' | 'letter' | 'memo'
interface ActionVariantConfig {
  actionType: string        // machine value sent in the payload
  title: (clientName: string) => string
  actionPlaceholder: string
  notePlaceholder: string
  // Displayed in the type-picker step (the CREATE-time
  // "New activity for X" screen that lists every variant as a
  // tappable row). Kept separate from title() because the
  // picker row wants a short verb phrase (Make call, Letter,
  // Memo) whereas title() is a full modal header ("Make call ·
  // {name}") that mixes verb + client context.
  pickerLabel: string
  // Whether to render the CLIENT + PHONE context strip when
  // this variant is active. Only 'call' opts in for now — a
  // letter or memo doesn't need a phone number at hand, so
  // rendering the strip would be visual noise. Future variants
  // (attendance-note, take-call) will choose per-case.
  showPhone: boolean
}
// The `actionType` (stored value) and `pickerLabel` are DERIVED from
// the shared ACTION_TYPES source of truth (src/constants) — so the
// picker's options + stored values can't drift from the filter and
// colour map. The bespoke per-variant UX copy (title, placeholders,
// showPhone) stays local: each type wants its own hint text, which
// isn't part of the shared vocabulary. Indexing ACTION_TYPE_BY_VALUE
// with a value not in ACTION_TYPES is a compile error, so these four
// are guaranteed to reference real vocabulary entries.
export const ACTION_VARIANTS: Record<ActionVariantKey, ActionVariantConfig> = {
  call: {
    actionType: ACTION_TYPE_BY_VALUE.call.value,
    pickerLabel: ACTION_TYPE_BY_VALUE.call.pickerLabel,
    title: (name) => `Make call · ${name}`,
    actionPlaceholder: 'e.g. Call to client re: medical appointment',
    notePlaceholder: 'What was discussed…',
    showPhone: true,
  },
  takeCall: {
    actionType: ACTION_TYPE_BY_VALUE.take_call.value,
    pickerLabel: ACTION_TYPE_BY_VALUE.take_call.pickerLabel,
    title: (name) => `Take call · ${name}`,
    actionPlaceholder: 'e.g. Call from client re: medical appointment',
    notePlaceholder: 'What was discussed…',
    showPhone: true,
  },
  letter: {
    actionType: ACTION_TYPE_BY_VALUE.letter.value,
    pickerLabel: ACTION_TYPE_BY_VALUE.letter.pickerLabel,
    title: (name) => `Letter · ${name}`,
    actionPlaceholder: 'e.g. Letter to client re: settlement offer',
    notePlaceholder: 'Summary of the letter…',
    showPhone: false,
  },
  memo: {
    actionType: ACTION_TYPE_BY_VALUE.memo.value,
    pickerLabel: ACTION_TYPE_BY_VALUE.memo.pickerLabel,
    title: (name) => `Memo · ${name}`,
    actionPlaceholder: 'e.g. Memo to file re: liability review',
    notePlaceholder: 'The memo…',
    showPhone: false,
  },
  // NOTE: 'email' is deliberately NOT a manual-activity variant. The single
  // email path is the client toolbar's Send email button (which sends via
  // Graph + logs the 'email' activity itself). 'email' stays in the shared
  // ACTION_TYPES vocab (timeline colour + filter + the send-flow's actionType)
  // — it's just not offered here in "+ New activity".
}

// Correspondent type options for the optional "who was this with?"
// dropdown. Value = label, stored verbatim as Activity.correspondentType
// (a loose taxonomy — there's no parties model to resolve an id against
// yet, so correspondentId stays null). Ordered to mirror the case's
// party groups: Client → Defendant → medical → insurer → GP/Hospital →
// Witness → Court → Other.
const CORRESPONDENT_TYPES = [
  'Client', 'Defendant', 'Medical agency', 'Medical expert', 'Insurer',
  'GP', 'Hospital', 'Witness', 'Court', 'Other',
] as const

// (The MM:SS clock + completed-tenths units helpers now live in
// emailClientLayout, shared with the ComposeEmailModal billing bar so the two
// readouts can't diverge.)

// Same shape ClientDocumentUploadModal uses for its case
// dropdown — defined inline so both modals + ClientSearchView
// can pass the same ClientCaseRow[] array without a shared
// types-file round-trip. Future cleanup could lift this to
// src/types/client.types.ts.
interface ClientCaseRow {
  id: string
  accidentDate: string | null
  closeDate: string | null
  setupFinalised: boolean
  updatedAt: string
  // THE case reference (SURNAME-YEAR-ID), precomputed by the parent (caseRef).
  ref?: string
  // The case NAME (shared caseName rule), shown beside the ref when given.
  name?: string
}

interface AddActivityModalProps {
  isOpen: boolean
  onClose: () => void
  clientId: string
  clientDisplayName: string
  cases: ClientCaseRow[]
  onCreated: () => void
  // When provided, the modal is in EDIT mode: fields pre-fill
  // from this row and submit calls updateActivityApi. When
  // absent / null, the modal is in CREATE mode (the original
  // behaviour). `onCreated` is reused for both — it's the
  // parent's refetch trigger.
  existingActivity?: Activity | null
  // Optional action-variant selector. Absent / null → generic
  // CREATE (byte-identical to the pre-variant path); a key →
  // pull the config out of ACTION_VARIANTS to retitle, swap
  // placeholders, stamp payload.actionType, and render the
  // variant's context strip (see clientPhone for 'call'). EDIT
  // mode ignores this entirely — variants only apply to new
  // rows.
  variant?: ActionVariantKey | null
  // Optional client phone for the 'call' variant's context
  // strip. Undefined / null / '' → "No number on file" text
  // fallback (styled quiet); populated → rendered as a tel:
  // link. No other variants read this yet.
  clientPhone?: string | null
  // Email is NOT a timed-activity variant — it's the real compose flow. When
  // provided, the picker shows an "Email" row that (instead of opening the
  // timed form) calls this to hand off to the parent's ComposeEmailModal — the
  // SAME launch the toolbar "Send email" button uses. Omitted → no Email row
  // (e.g. the edit modal, which never shows the picker anyway).
  onPickEmail?: () => void
  // Optional: preselect this case on a NEW entry (the case page's Activity
  // section). Absent → '' (no specific case), the original behaviour. The
  // user can still change it; EDIT mode always uses the row's own caseId.
  defaultCaseId?: string | null
}

const AddActivityModal: React.FC<AddActivityModalProps> = ({
  isOpen,
  onClose,
  clientId,
  clientDisplayName,
  cases,
  onCreated,
  existingActivity,
  variant,
  clientPhone,
  onPickEmail,
  defaultCaseId,
}) => {
  // Derived mode flag. Kept as a const so the render + effects
  // read from a single source. `!!` coerces both null and
  // undefined to false — CREATE.
  const isEditMode = !!existingActivity
  // ★ A SENT EMAIL (actionType 'email' — only ever created by the send flow;
  // drafts are EmailDraft rows, resumed in the composer) is the record of
  // what was sent: its content is READ-ONLY here, only the billable TIME can
  // be corrected. The server enforces the same rule (activityController).
  const isSentEmail = isEditMode && existingActivity?.actionType === 'email'
  const actionInputRef = useRef<HTMLInputElement | null>(null)
  // Drift-free anchor for the running timer. When the timer
  // starts / resumes we stamp `startedAtMs = Date.now()` and
  // `baseSeconds = currentSeconds`. The tick then RECOMPUTES
  // currentSeconds = baseSeconds + floor((now - startedAtMs)/1000)
  // — so backgrounded / throttled setInterval callbacks catch up
  // to the real wall clock instead of accumulating drift.
  // Cleared to null on pause / manual-edit / close so the next
  // resume re-anchors from the current baseSeconds.
  const timerAnchorRef = useRef<{ startedAtMs: number; baseSeconds: number } | null>(null)

  // Form state. Kept as strings for the number-shaped input
  // (unitsInput) so a mid-typed "0." doesn't get parsed and
  // reformatted mid-keystroke. Parse happens once at submit.
  const [action, setAction] = useState<string>('')
  const [content, setContent] = useState<string>('')
  const [unitsInput, setUnitsInput] = useState<string>('')
  const [caseId, setCaseId] = useState<string>('')
  // Correspondent capture (optional). Both are free of validation —
  // empty type ('') and empty name ('') are valid and stored as-is;
  // submit is never gated on them. correspondentId is not captured
  // (no parties model to resolve an id against — stays null).
  const [correspondentType, setCorrespondentType] = useState<string>('')
  const [correspondentName, setCorrespondentName] = useState<string>('')

  // Timer state. `currentSeconds` is the ONE SOURCE OF TRUTH
  // for time in this modal — the clock reads
  // secondsToClock(currentSeconds), the units display reads
  // billableUnits(currentSeconds) (whole 6-min units, rounded up), and submit consumes it
  // as durationSeconds. `isTimerRunning` gates the tick effect.
  // `manualOverride` flips true while the user is typing in the
  // units box so the currentSeconds→unitsInput sync doesn't
  // stomp their in-progress keystrokes.
  const [currentSeconds, setCurrentSeconds] = useState<number>(0)
  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(false)
  const [manualOverride, setManualOverride] = useState<boolean>(false)

  // Submission state. `submitting` disables both buttons + the
  // fields; `error` renders inline below the field cluster.
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // ★ Casevo "timer running" popup (TimerLeaveDialog). Non-null while it's
  // showing. proceed/cancel are set when the popup was raised by an IN-APP
  // navigation (browser Back via timerNavGuard) rather than this modal's own
  // close buttons: proceed() continues that navigation after Save/Discard,
  // cancel() undoes it on "Keep timing".
  const [leavePrompt, setLeavePrompt] = useState<{ proceed?: () => void; cancel?: () => void } | null>(null)

  // ── PICKER STEP ──────────────────────────────────────────
  // Stage 2c. When the CREATE-instance opener passes no variant
  // prop (the header's "+ New activity" button), we FIRST render
  // a type-picker step listing every ACTION_VARIANTS entry, and
  // the timer is NOT running yet — the user must not be billed
  // for the seconds they spend deciding what they're about to
  // do. When they pick a row, chosenVariant is set, pickerOpen
  // flips false, and setIsTimerRunning(true) starts the clock at
  // the moment of choice.
  //
  // A direct opener that passes an explicit variant prop (e.g.
  // future toolbar buttons that jump straight into "Log call")
  // skips the picker entirely: pickerOpen initialises false and
  // the timer auto-starts, exactly the 2b behaviour.
  //
  // EDIT mode always has pickerOpen=false — variants are CREATE-
  // only, and the row's original actionType is preserved
  // untouched.
  const [pickerOpen, setPickerOpen] = useState<boolean>(false)
  const [chosenVariant, setChosenVariant] = useState<ActionVariantKey | null>(null)

  // Local, EDIT-ONLY-IN-MODAL phone value for the 'call' variant's
  // context strip. Prefilled from the clientPhone prop on open;
  // the input mutates ONLY this state — it is NEVER sent in the
  // create/update payload and NEVER writes back to the Client
  // record. Snapshot-model discipline: client contact data lives
  // on the Client record and is edited on the Client Details
  // section. This local copy exists so the fee-earner can correct
  // a mistyped digit or note a temporary number without touching
  // the client's source-of-truth mainContactNumber. Persistence
  // of the corrected number, if wanted later, is a separate
  // "update client" action, not a side effect of logging a call.
  const [phoneValue, setPhoneValue] = useState<string>('')

  // Resolved variant config for this render. EDIT mode always
  // gets null (variants only steer new rows; editing an existing
  // row must preserve its stored actionType + neutral chrome).
  // In CREATE, the effective variant is `variant ?? chosenVariant`
  // — either a direct-opener key from the parent, or the one the
  // user picked in the picker step. If both are null (parent
  // opened generic AND user hasn't picked yet), variantCfg is
  // null — the picker itself renders based on pickerOpen; no
  // form / no header strip is rendered in that state.
  const effectiveVariant: ActionVariantKey | null =
    !isEditMode ? (variant ?? chosenVariant) : null
  const variantCfg: ActionVariantConfig | null =
    effectiveVariant ? ACTION_VARIANTS[effectiveVariant] : null

  // Reset all form state on open. Three branches (Stage 2c):
  //   CREATE + no variant prop → PICKER step: blank fields,
  //     timer at 0 and NOT running (must not bill the user for
  //     time spent deciding). Timer only starts when they pick
  //     a row in the picker (see the choose handler below).
  //   CREATE + variant prop → DIRECT-VARIANT open (future
  //     toolbar): skip the picker, timer auto-starts exactly as
  //     the 2b behaviour — the caller pre-committed to a type.
  //   EDIT mode → pre-fill text fields from `existingActivity`;
  //     timer loads at existing.durationSeconds and stays PAUSED
  //     (so opening an old entry doesn't retroactively add
  //     time). caseId null → ''. Picker is never shown in EDIT.
  // `unitsInput` is now DERIVED from currentSeconds via the
  // sync effect below — no explicit setUnitsInput call here.
  // Phone: seeded from clientPhone in CREATE only; local edits
  // stay local (see phoneValue state's docblock).
  // Also captures the previously-focused element for restore-
  // on-close.
  useEffect(() => {
    if (isOpen) {
      if (existingActivity) {
        setAction(existingActivity.action)
        setContent(existingActivity.content)
        setCaseId(existingActivity.caseId ?? '')
        setCurrentSeconds(existingActivity.durationSeconds)
        setIsTimerRunning(false)   // EDIT loads paused, per spec
        setPickerOpen(false)       // variants + picker are CREATE-only
        setChosenVariant(null)
        setCorrespondentType(existingActivity.correspondentType ?? '')
        setCorrespondentName(existingActivity.correspondentName ?? '')
      } else {
        setAction('')
        setContent('')
        setCaseId(defaultCaseId ?? '')
        setCurrentSeconds(0)
        // CREATE branching: no variant prop → picker step, timer
        // MUST NOT run while choosing. Variant prop present →
        // skip the picker and auto-start the clock (2b path).
        if (variant) {
          setPickerOpen(false)
          setIsTimerRunning(true)
        } else {
          setPickerOpen(true)
          setIsTimerRunning(false)
        }
        setChosenVariant(null)
        setCorrespondentType('')
        setCorrespondentName('')
        // Seed the editable phone with the client's current
        // mainContactNumber. Edits from here on are LOCAL — see
        // phoneValue's docblock. Empty / null / undefined all
        // land as ''.
        setPhoneValue(clientPhone ?? '')
      }
      setManualOverride(false)
      timerAnchorRef.current = null
      setSubmitting(false)
      setError(null)
      setLeavePrompt(null)
    }
    return () => {
      if (isOpen) {
        // On unmount / close: tear down the anchor (the tick effect
        // itself is gated on isOpen so it stops naturally). (Focus
        // restore to the opener is ModalFrame's now.)
        timerAnchorRef.current = null
        // ROOT reopen-race fix: zero timer state on EVERY close
        // path (Discard, clean Esc/Cancel/backdrop, post-save,
        // parent-driven close). The parent keeps this component
        // MOUNTED across isOpen toggles, so persisted state
        // (currentSeconds, isTimerRunning) would otherwise leak
        // into the next open — the tick effect anchors from
        // render-scope currentSeconds during the reopen commit,
        // BEFORE the reset-on-open setters commit, so a non-zero
        // persisted currentSeconds becomes the new anchor's
        // baseSeconds and the clock resumes from the old value.
        // Zeroing here in the CLOSE-edge cleanup (not the open
        // branch) means Render 1 of every reopen already sees
        // currentSeconds=0, killing the race regardless of effect
        // ordering. setState in cleanup is safe: the instance
        // does NOT unmount (only isOpen flips), so no "setState
        // on unmounted component" warning applies.
        setIsTimerRunning(false)
        setCurrentSeconds(0)
      }
    }
  }, [isOpen, existingActivity, variant, clientPhone, defaultCaseId])

  // ── DRIFT-FREE TIMER TICK ──────────────────────────────────
  // The tick RECOMPUTES currentSeconds from a real-time anchor
  // instead of incrementing by 1 each interval. This means:
  //   - background/throttled setInterval callbacks catch up to
  //     the wall clock on their next fire (rather than losing
  //     seconds silently);
  //   - the displayed clock stays accurate even if the browser
  //     tab was inactive for a minute;
  //   - the poll rate (250ms → 4x/sec) is fast enough for a
  //     smooth-looking clock but cheap on the event loop.
  // Anchor is created on first tick of a run (if not already
  // set by handleStartTimer) and cleared on pause / manual
  // edit / close so the next resume re-anchors from the
  // current baseSeconds.
  useEffect(() => {
    if (!isOpen || !isTimerRunning) return
    if (!timerAnchorRef.current) {
      timerAnchorRef.current = { startedAtMs: Date.now(), baseSeconds: currentSeconds }
    }
    const id = setInterval(() => {
      const anchor = timerAnchorRef.current
      if (!anchor) return
      const elapsed = Math.floor((Date.now() - anchor.startedAtMs) / 1000)
      setCurrentSeconds(anchor.baseSeconds + elapsed)
    }, 250)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isTimerRunning])
  // ^ currentSeconds intentionally NOT in the dep array — it's
  // only READ inside the effect to seed the anchor on the first
  // run, and re-running the effect on every tick would tear
  // down + re-create the interval every second (and re-anchor,
  // which would corrupt the elapsed calculation). The anchor
  // gets refreshed via handleStartTimer / handlePauseTimer /
  // the manual box's onChange, all of which set
  // timerAnchorRef.current = null so the NEXT tick of a fresh
  // run re-anchors from the new baseSeconds.

  // ── SYNC currentSeconds → unitsInput ───────────────────────
  // The units input is a DERIVED display of currentSeconds
  // unless the user is actively typing in it (manualOverride
  // === true). This keeps the visible number in step with the
  // clock without stomping mid-keystroke input.
  //
  // 6-MINUTE UNITS, ROUNDED UP (UK legal billing standard —
  // utils/billingUnits, twin of the backend lib/billing.ts):
  // 0s → 0, 1s–6:00 → 1, 6:01–12:00 → 2 … The number shown is
  // exactly the units the server charges (units × rate ÷ 10).
  // The clock (secondsToClock) remains exact to the second; the
  // saved durationSeconds is the exact currentSeconds int.
  useEffect(() => {
    if (!manualOverride) {
      // 6-minute units ROUNDED UP — exactly what the server will charge.
      setUnitsInput(String(billableUnits(currentSeconds)))
    }
  }, [currentSeconds, manualOverride])

  // ── START / PAUSE HANDLERS ─────────────────────────────────
  // Start clears manualOverride (so the tick-sync resumes
  // writing to unitsInput) and clears the anchor (so the next
  // tick re-anchors from the current currentSeconds — critical
  // when resuming after a manual edit that changed baseSeconds).
  const handleStartTimer = (): void => {
    setManualOverride(false)
    timerAnchorRef.current = null
    setIsTimerRunning(true)
  }
  // Pause clears the anchor + flips the running flag. The tick
  // effect's cleanup fires via the dep change and clears its
  // interval. currentSeconds keeps its last value — the clock
  // stays frozen at the paused time.
  const handlePauseTimer = (): void => {
    // Flush the EXACT elapsed time before clearing the anchor — the last
    // 250ms tick can lag by up to ~1s, which was lost on every pause.
    setCurrentSeconds(liveSeconds())
    timerAnchorRef.current = null
    setIsTimerRunning(false)
  }
  // Exact seconds right now: from the wall-clock anchor while running,
  // else the stored value.
  const liveSeconds = (): number => {
    const a = timerAnchorRef.current
    return isTimerRunning && a ? a.baseSeconds + Math.floor((Date.now() - a.startedAtMs) / 1000) : currentSeconds
  }
  // Manual units override (same behaviour as before, now driven by the shared
  // BillingBar): any keystroke pauses the timer and takes ownership of
  // currentSeconds (units × 360s).
  const handleUnitsInputChange = (raw: string): void => {
    setUnitsInput(raw)
    setManualOverride(true)
    timerAnchorRef.current = null
    setIsTimerRunning(false)
    const parsed = parseFloat(raw)
    if (Number.isFinite(parsed) && parsed >= 0) {
      // ★ Manual units (fee-earner judgment, e.g. "one email to many = 1
      // unit") are stored as units × 360s, which the server's ceil rule maps
      // back to exactly these units → charge = units × (rate ÷ 10). A typed
      // fraction rounds UP to the next whole unit (units are whole).
      setCurrentSeconds(unitsToSeconds(parsed))
    } else if (raw.trim() === '') {
      // Cleared box = 0 units. Previously the OLD seconds stayed and were
      // saved while the box looked empty.
      setCurrentSeconds(0)
    }
  }

  // Picker-row choose handler. Fires when the user taps one of
  // the type-picker rows. Three effects, in this order:
  //   (1) stamp the chosen variant so downstream reads
  //       (variantCfg, title, placeholders, phone strip,
  //       payload.actionType) all pick it up on the next
  //       render;
  //   (2) close the picker (which switches the body from the
  //       picker rows to the form);
  //   (3) START THE TIMER — the clock begins running AT THE
  //       MOMENT OF CHOICE, not before. The seconds spent
  //       deciding the type are never billed.
  // We also clear the anchor so the tick effect re-anchors from
  // baseSeconds=0 on its next fire (belt-and-braces — the reset-
  // on-open effect already cleared it, but this makes the
  // start-from-zero contract explicit at the choice site).
  const handleChooseVariant = (key: ActionVariantKey): void => {
    setChosenVariant(key)
    setPickerOpen(false)
    timerAnchorRef.current = null
    setIsTimerRunning(true)
  }

  // ── BILLABLE ONLY ON A CASE (shared rule + UI: ./NotBillable) ──
  // Linking a case (here, now or on a later edit) makes it billable. A sent
  // email's case is fixed (read-only below).
  const effectiveCaseId = isSentEmail ? (existingActivity?.caseId ?? '') : caseId
  const billableType = entryTypeFor(isEditMode ? existingActivity?.actionType : (variantCfg?.actionType ?? null)).bills
  const chargeableWithoutCase = (seconds: number): boolean => isChargeableWithoutCase(billableType, seconds, effectiveCaseId)
  // Live, for the red inline text on the case field (tracks the timer).
  const notBillableNow = chargeableWithoutCase(currentSeconds)
  // The "save as non-billable?" confirm. `leave` = the timer-leave prompt that
  // asked to save, so its navigation resumes (or is cancelled) after the answer.
  const [confirmNoCase, setConfirmNoCase] = useState<{ leave?: { proceed?: () => void; cancel?: () => void } | null } | null>(null)

  // ── SUBMIT ───────────────────────────────────────────────────
  // nonBillableOk: the user already confirmed saving chargeable time with no case.
  const handleSubmit = async (opts: { nonBillableOk?: boolean } = {}): Promise<boolean> => {
    if (submitting) return false
    setError(null)
    // ── Validate ──────────────────────────────────────────────
    const trimmedAction = action.trim()
    if (trimmedAction.length === 0) {
      setError('Action is required')
      return false
    }
    // ── currentSeconds is already the source of truth ─────────
    // Everything time-related has flowed through it: the tick
    // recomputes it from the real-time anchor; manual box edits
    // pause the timer and write back to it; the seconds↔units
    // sync effect derives the displayed number from it. So the
    // payload's durationSeconds is just this int, no parse-at-
    // submit needed. Defensive negative guard shouldn't ever
    // fire but keeps the shape symmetrical with the earlier
    // "invalid time" branch.
    if (manualOverride && unitsInput.trim() !== '' && !Number.isFinite(parseFloat(unitsInput))) {
      setError('Units must be a number')
      return false
    }
    const durationSeconds = liveSeconds()
    if (durationSeconds < 0) {
      setError('Invalid time')
      return false
    }
    // Same 24h single-entry cap the server enforces — a friendly message
    // instead of the raw API error.
    if (durationSeconds > 86400) {
      setError('A single entry can be at most 24 hours (240 units). Please split it into separate entries.')
      return false
    }
    // Chargeable time but no case → ask first (it won't be billable).
    if (!opts.nonBillableOk && chargeableWithoutCase(durationSeconds)) {
      setConfirmNoCase({})
      return false
    }
    // Stop the timer on submit start for tidiness — the tick
    // effect would tear down on unmount anyway, but this
    // avoids a stray final tick advancing the display while
    // the network call is in flight.
    setIsTimerRunning(false)
    timerAnchorRef.current = null
    // ── Build payload ─────────────────────────────────────────
    // caseId '' → null (explicit "no specific case"). content
    // trims empty → undefined in CREATE so the backend's default
    // '' kicks in without sending an empty string; in EDIT we
    // send content verbatim (the trimmed string, even if empty)
    // so clearing the field actually clears the row's content.
    const trimmedContent = content.trim()
    setSubmitting(true)
    try {
      if (isEditMode && existingActivity) {
        // EDIT: PATCH with the four editable fields. Server
        // whitelists action/content/durationSeconds/caseId and
        // silently strips everything else; if durationSeconds
        // changed it recomputes amountPence from the row's
        // FROZEN hourlyRatePenceAtEntry (never a fresh
        // GradeRate lookup) — see backend activityController.ts.
        if (isSentEmail) {
          // Sent email → time only (the content is the sent record).
          await updateActivityApi(existingActivity.id, { durationSeconds })
        } else await updateActivityApi(existingActivity.id, {
          action: trimmedAction,
          content: trimmedContent,
          durationSeconds,
          caseId: caseId || null,
          // Correspondent — sent verbatim (empty string clears it).
          correspondentType,
          correspondentName: correspondentName.trim(),
        })
      } else {
        // CREATE: clientId is required by the payload type;
        // snapshot fields (actor triple, gradeAtEntry,
        // hourlyRatePenceAtEntry, amountPence) are stamped
        // server-side.
        //
        // actionType: spread from the EFFECTIVE variant's config
        // (variant prop ?? chosenVariant, resolved into
        // variantCfg above). In Stage 2c every CREATE from the UI
        // has a variant by the time we reach here: either the
        // parent passed a direct-variant prop (future toolbar) or
        // the user chose one in the picker step (which is the
        // only way to reach the form when no direct prop was
        // passed). The conditional spread nevertheless keeps the
        // defensive shape: if variantCfg is null we send NO
        // actionType key at all — the backend's `typeof
        // actionType === 'string' ? actionType : ''` guard then
        // leaves it as its schema default. Absent-vs-empty
        // asymmetry mirrors the content undefined-vs-'' pattern
        // above.
        await createActivityApi({
          clientId,
          caseId: caseId || null,
          action: trimmedAction,
          content: trimmedContent.length > 0 ? trimmedContent : undefined,
          durationSeconds,
          // Correspondent — optional; empty strings are fine and never
          // block submit. correspondentId not sent (stays null).
          correspondentType,
          correspondentName: correspondentName.trim(),
          ...(variantCfg ? { actionType: variantCfg.actionType } : {}),
        })
      }
      // Same callback for both — the parent refetches its list +
      // running total, so a create appears at the top and an
      // edit shows the updated row in place.
      onCreated()
      onClose()
      return true
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : (isEditMode ? 'Failed to save changes' : 'Failed to add activity')
      setError(message)
      return false
    } finally {
      setSubmitting(false)
    }
  }

  // Central close-attempt gate. All three close paths (Cancel,
  // Esc, backdrop) call THIS instead of onClose() directly. With a
  // running timer or unsaved create-mode work it raises the Casevo
  // TimerLeaveDialog (Save entry / Discard / Keep timing); with
  // nothing to lose it closes immediately.
  // Edit mode: the entry's fields changed since it opened (explicit-save
  // form — part of the app-wide unsaved-changes rule; same popup).
  const editDirty = useDraftDirty(isOpen && isEditMode, [action, content, caseId, correspondentType, correspondentName])
  const isDirty = (): boolean =>
    (isEditMode && editDirty) || (!isEditMode && (
      action.trim() !== '' ||
      content.trim() !== '' ||
      currentSeconds >= 36
    ))
  // ★ A running/unsaved timer lives only in React state — a reload or tab
  // close would lose it silently. Browser leave-page prompt while the modal
  // is open and EITHER the timer is running (any elapsed time, create OR
  // edit mode) OR there's unsaved create-mode work. Previously this was gated
  // on isDirty() alone, which is false for the first 36s of a run and always
  // false in edit mode — so a running timer usually got no warning.
  useLeavePageWarning(isOpen && !submitting && (isTimerRunning || isDirty()))
  // Unsaved time worth asking about: a running timer (any mode, any
  // elapsed time) or unsaved create-mode work (text / ≥ 0.1 unit).
  const needsLeavePrompt = isTimerRunning || isDirty()
  // In-app navigation (browser Back/Forward) while time is unsaved → raise
  // the same popup instead of silently unmounting this modal.
  useEffect(() => {
    if (!isOpen || submitting || !needsLeavePrompt) return
    return registerTimerNavGuard((proceed, cancel) => setLeavePrompt({ proceed, cancel }))
  }, [isOpen, submitting, needsLeavePrompt])
  const handleAttemptClose = (): void => {
    if (submitting) return
    // Popup already up (e.g. Esc pressed twice) → treat as "Keep timing".
    if (leavePrompt) { handleLeaveKeep(); return }
    if (needsLeavePrompt) {
      // The timer is NOT paused — "Keep timing" must leave it running.
      setLeavePrompt({})
      return
    }
    onClose()
  }
  const handleLeaveSave = async (): Promise<void> => {
    const p = leavePrompt
    setLeavePrompt(null)
    // No case on chargeable time → the "save as non-billable?" confirm first;
    // its answer then resumes (or cancels) this leave.
    if (action.trim() && chargeableWithoutCase(liveSeconds())) { setConfirmNoCase({ leave: p }); return }
    // handleSubmit validates (e.g. "Action is required" shows inline and the
    // modal stays open), saves the exact live seconds, then closes itself.
    const ok = await handleSubmit()
    if (ok) p?.proceed?.()
    else p?.cancel?.()
  }
  // "Save as non-billable" → save without the case check; "Go back" → stay in
  // the form (and cancel any leave that asked to save).
  const handleConfirmNoCase = async (): Promise<void> => {
    const c = confirmNoCase
    setConfirmNoCase(null)
    const ok = await handleSubmit({ nonBillableOk: true })
    if (c?.leave) { if (ok) c.leave.proceed?.(); else c.leave.cancel?.() }
  }
  const handleNoCaseGoBack = (): void => {
    const c = confirmNoCase
    setConfirmNoCase(null)
    c?.leave?.cancel?.()
  }
  const handleLeaveDiscard = (): void => {
    const p = leavePrompt
    setLeavePrompt(null)
    timerAnchorRef.current = null
    setIsTimerRunning(false)
    onClose()
    p?.proceed?.()
  }
  function handleLeaveKeep(): void {
    const p = leavePrompt
    setLeavePrompt(null)
    p?.cancel?.()
  }
  const handleCancel = (): void => {
    handleAttemptClose()
  }

  if (!isOpen) return null

  // ★ PICKER STEP = the light compact card (approved Option B fallback):
  // the bare-ModalFrame type tiles, no heavy green-header modal. Reached
  // only by a variant-less open with no anchor button (the feeds' "Log
  // time" now picks inline via the popover, ActivityTypePicker.tsx). The
  // timer still starts only on a pick (handleChooseVariant).
  if (pickerOpen) {
    return (
      <ActivityTypePickerCard
        isOpen
        onClose={onClose}
        onPick={handleChooseVariant}
        onEmail={onPickEmail}
        subtitle={clientDisplayName}
      />
    )
  }

  // ── Presentational (approved mockup, Option A — same frame as the
  // redesigned Compose email + the read-only ActivityViewModal) ──
  const entryType = isEditMode ? existingActivity!.actionType : (variantCfg?.actionType ?? null)
  const entryDef = entryTypeFor(entryType)
  const caseRefOf = (id: string): string => {
    const c = cases.find((x) => x.id === id)
    return c ? (c.ref ?? caseShortId(c.id)) : ''
  }
  const headerRef = caseId ? caseRefOf(caseId) : ''
  // "Name · REF" (or "Case REF" when the caller didn't pass a name).
  const caseLabelOf = (id: string): string => {
    const c = cases.find((x) => x.id === id)
    const ref = caseRefOf(id) || caseShortId(id)
    return c?.name ? `${c.name} · ${ref}` : `Case ${ref}`
  }
  const telHref = telHrefOf(phoneValue)
  const waHref = whatsAppHrefOf(phoneValue)
  const title = isEditMode
    ? (existingActivity!.action || 'Edit activity')
    : variantCfg
      ? variantCfg.title(clientDisplayName)
      : `Activity · ${clientDisplayName}`
  const subtitle = isSentEmail
    ? `Sent ${new Date(existingActivity!.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} by ${existingActivity!.actorName} · the email is the record of what was sent — only its time can be corrected`
    : isEditMode
    ? `Logged ${new Date(existingActivity!.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} by ${existingActivity!.actorName} · changes keep the original type and date`
    : 'Logged on the client’s activity with this time. Billed at your rate when you save.'

  return (
    <>
      <ModalFrame
        isOpen
        onClose={handleCancel}
        dismissible={!submitting}
        size="xl"
        scrollBody
        eyebrow={isSentEmail ? 'Sent email · edit time' : isEditMode ? 'Edit activity' : (variantCfg ? variantCfg.pickerLabel : 'Activity')}
        eyebrowIcon={isSentEmail ? <UiIconSvg icon="lock" size={14} /> : isEditMode ? <UiIconSvg icon="edit" size={14} /> : <EntryIconSvg icon={entryDef.icon} size={14} />}
        pills={headerRef ? <span className={HERO_PILL_SOFT}>{headerRef}</span> : undefined}
        title={title}
        subtitle={subtitle}
        initialFocusRef={actionInputRef}
        bodyClassName="flex flex-col gap-[14px]"
        footer={
          <div className="flex flex-wrap items-center gap-[10px] w-full">
            {/* Compact billing control (was the full-width BillingBar). */}
            <TimerControl
              currentSeconds={currentSeconds}
              isTimerRunning={isTimerRunning}
              onToggleTimer={isTimerRunning ? handlePauseTimer : handleStartTimer}
              unitsInput={unitsInput}
              onUnitsInputChange={handleUnitsInputChange}
              disabled={submitting}
            />
            <div className="ml-auto flex items-center gap-[10px]">
              <button
                type="button"
                onClick={handleCancel}
                disabled={submitting}
                className="inline-flex items-center gap-[7px] text-[14px] font-medium rounded-[8px] px-4 py-[10px] border border-casevo-border bg-transparent text-casevo-mid hover:border-casevo-mid hover:text-casevo-forest transition-colors focus:outline-none casevo-focus disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { void handleSubmit() }}
                disabled={submitting}
                className={`${BTN_PRIMARY} text-[14px] rounded-[8px] px-4 py-[10px] border border-casevo-primary`}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-[15px] h-[15px] shrink-0" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                {isEditMode
                  ? (submitting ? 'Saving…' : 'Save changes')
                  : (submitting ? 'Saving…' : 'Save entry')}
              </button>
            </div>
          </div>
        }
      >
        {/* Call strip (CLIENT + editable PHONE) — 'call' / 'takeCall' opt in
            via cfg.showPhone. Local-only phone: never sent, never writes back
            to the Client record. */}
        {variantCfg && variantCfg.showPhone && (
          <div className={`${CARD} shrink-0 flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-[22px] px-4 py-3`}>
            <div className="flex flex-col gap-[5px] min-w-0">
              <span className={LABEL}>Client</span>
              <b className="text-[14.5px] font-semibold text-casevo-forest truncate">{clientDisplayName}</b>
            </div>
            <div className="flex flex-col gap-[5px] min-w-0 flex-1">
              <span className={LABEL}>Phone <i className="not-italic normal-case tracking-normal font-medium">· local only, not saved to the client</i></span>
              <div className="flex items-center gap-2 rounded-[8px] border border-casevo-border bg-casevo-input pl-[10px] pr-[6px] py-[6px] focus-within:border-casevo-mid">
                <span className="text-casevo-text-soft"><PhoneIcon /></span>
                <input
                  type="tel"
                  value={phoneValue}
                  onChange={(e) => setPhoneValue(e.target.value)}
                  disabled={submitting}
                  placeholder="No number on file"
                  aria-label="Phone number (local, not saved to client)"
                  className="flex-1 min-w-0 bg-transparent border-0 p-0 text-[14px] text-casevo-forest tabular-nums placeholder:text-casevo-text-soft focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                />
                {/* ★ Number-specific links. The raw `tel:${typed}` (spaces,
                    national 0) opened WhatsApp Desktop — a common tel:
                    handler — WITHOUT the contact's chat. Now: Call = an E.164
                    tel: link; WhatsApp = wa.me/<international digits>, which
                    opens THAT number's conversation. Missing/invalid number →
                    no broken link, just a hint. (phoneLinks.ts) */}
                {phoneValue.trim() !== '' && (telHref && waHref ? (
                  <>
                    <a
                      href={telHref}
                      title={`Call ${phoneValue.trim()}`}
                      className={CALL_BTN}
                    >
                      <PhoneIcon small />Call
                    </a>
                    <a
                      href={waHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Open WhatsApp chat with ${phoneValue.trim()}`}
                      className={CALL_BTN}
                    >
                      <WhatsAppGlyph />WhatsApp
                    </a>
                  </>
                ) : (
                  <span className="shrink-0 text-[12px] text-casevo-danger" role="status">Check the number</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Sent email — the content is the sent record: read-only. */}
        {isSentEmail && existingActivity && (
          <>
            <div className="shrink-0 flex items-start gap-[10px] rounded-[12px] px-[14px] py-[10px] text-[13px] text-casevo-forest bg-casevo-input border border-casevo-border">
              <span className="mt-px text-casevo-brand dark:text-[#8fd6b0]"><UiIconSvg icon="lock" size={15} /></span>
              <span><b className="font-semibold">This email has been sent.</b> Its subject, recipients and message are the record of what was sent and can’t be changed. You can correct the billable time below.</span>
            </div>
            <div className={`${CARD} shrink-0`}>
              <div className={ROW}><span className={ROW_LABEL}>Subject</span><span className="flex-1 min-w-0 py-3 text-[14.5px] font-semibold text-casevo-forest break-words">{existingActivity.action}</span></div>
              {(existingActivity.correspondentType || existingActivity.correspondentName) && (
                <div className={ROW}><span className={ROW_LABEL}>With</span><span className="flex-1 min-w-0 py-3 text-[14px] text-casevo-forest break-words">{[existingActivity.correspondentType, existingActivity.correspondentName].filter(Boolean).join(' · ')}</span></div>
              )}
              <div className={ROW}><span className={ROW_LABEL}>Case</span><span className="flex-1 min-w-0 py-3 text-[14px] text-casevo-forest">{existingActivity.caseId ? caseLabelOf(existingActivity.caseId) : <><span className="text-casevo-text-soft">No specific case</span>{notBillableNow && <NotBillableNote id="activity-case-not-billable" className="block mt-[2px]">Not billable — this sent email isn’t linked to a case.</NotBillableNote>}</>}</span></div>
            </div>
            {existingActivity.content && (
              <div className={`${CARD} shrink-0 overflow-hidden`}>
                <div className="px-4 py-[10px] bg-casevo-input border-b border-casevo-border-soft text-[10.5px] font-bold tracking-[0.16em] uppercase text-casevo-text-soft">Note</div>
                <p className="m-0 px-5 py-4 text-[14px] leading-[1.6] text-casevo-forest whitespace-pre-wrap break-words">{existingActivity.content}</p>
              </div>
            )}
          </>
        )}

        {/* Fields card — Action / With / Case. */}
        {!isSentEmail && (<>
        <div className={`${CARD} shrink-0`}>
          <label className={ROW}>
            <span className={ROW_LABEL}>Action</span>
            <input
              ref={actionInputRef}
              type="text"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              disabled={submitting}
              placeholder={variantCfg ? variantCfg.actionPlaceholder : 'e.g. Letter, Call, Memo'}
              className={`${ROW_INPUT} !text-[14.5px] font-semibold`}
            />
          </label>
          <div className={`${ROW} flex-wrap py-2`}>
            <span className={ROW_LABEL}>With</span>
            <select
              value={correspondentType}
              onChange={(e) => setCorrespondentType(e.target.value)}
              disabled={submitting}
              aria-label="Correspondent type"
              className={SELECT}
            >
              <option value="">— None</option>
              {CORRESPONDENT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <input
              type="text"
              value={correspondentName}
              onChange={(e) => setCorrespondentName(e.target.value)}
              disabled={submitting}
              placeholder="e.g. Bright Medical Ltd (optional)"
              aria-label="Correspondent name"
              className={`${ROW_INPUT} !py-[6px]`}
            />
          </div>
          <label className={`${ROW} py-2 flex-wrap`}>
            <span className={ROW_LABEL}>Case</span>
            <select
              value={caseId}
              onChange={(e) => setCaseId(e.target.value)}
              disabled={submitting}
              aria-invalid={notBillableNow || undefined}
              aria-describedby={notBillableNow ? 'activity-case-not-billable' : undefined}
              className={`${SELECT} flex-1 min-w-0 ${notBillableNow ? '!border-casevo-danger' : ''}`}
            >
              <option value="">No specific case</option>
              {cases.map((c) => (
                <option key={c.id} value={c.id}>
                  {`${caseLabelOf(c.id)}${c.accidentDate ? ` · Accident: ${formatDateUK(c.accidentDate)}` : ''}`}
                </option>
              ))}
            </select>
            {/* Chargeable time with no case → it won't be billable (it can't enter WIP). */}
            {notBillableNow && <NotBillableNote id="activity-case-not-billable" className="basis-full pl-[78px] -mt-1 pb-1" />}
          </label>
        </div>

        {/* Note card — plain text (unchanged model), optional. */}
        <label className={`${CARD} shrink-0 overflow-hidden flex flex-col`}>
          <span className="flex items-baseline gap-2 px-4 py-[10px] bg-casevo-input border-b border-casevo-border-soft">
            <b className="text-[10.5px] font-bold tracking-[0.16em] uppercase text-casevo-text-soft">Note</b>
            <span className="text-[12px] text-casevo-text-soft">optional · plain text</span>
          </span>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={submitting}
            placeholder={variantCfg ? variantCfg.notePlaceholder : 'Description of the work…'}
            className="w-full min-h-[200px] resize-y border-0 bg-transparent px-5 py-4 text-[14px] leading-[1.6] text-casevo-forest placeholder:text-casevo-text-soft focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
          />
        </label>
        </>)}

        {/* Inline error (validation / save failure). */}
        {error && <p className="shrink-0 m-0 text-[13px] text-casevo-danger" role="alert">{error}</p>}
      </ModalFrame>
      <TimerLeaveDialog
        isOpen={leavePrompt !== null}
        seconds={currentSeconds}
        running={isTimerRunning}
        saveLabel={isEditMode ? 'Save changes' : 'Save entry'}
        busy={submitting}
        onSave={() => { void handleLeaveSave() }}
        onDiscard={handleLeaveDiscard}
        onKeep={handleLeaveKeep}
      />
      <NonBillableConfirm
        isOpen={confirmNoCase !== null}
        onConfirm={() => { void handleConfirmNoCase() }}
        onCancel={handleNoCaseGoBack}
      />
    </>
  )
}

// ── Presentational constants (shared look with ComposeEmailModal) ──
const CARD = 'bg-casevo-surface border border-casevo-border rounded-[14px]'
const ROW = 'flex items-center gap-[14px] px-4 min-h-[46px] border-t border-casevo-border-soft first:border-t-0'
const ROW_LABEL = 'w-16 shrink-0 text-[10.5px] font-semibold tracking-[0.16em] uppercase text-casevo-text-soft'
const ROW_INPUT = 'flex-1 min-w-0 bg-transparent border-0 p-0 py-3 text-[14px] text-casevo-forest placeholder:text-casevo-text-soft focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed'
const SELECT = 'border border-casevo-border rounded-[8px] px-[10px] py-[5px] text-[13px] bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid disabled:opacity-60'
const CALL_BTN = 'shrink-0 inline-flex items-center gap-[5px] text-[12px] font-semibold text-casevo-brand dark:text-[#8fd6b0] border border-casevo-border bg-casevo-surface rounded-[6px] px-[9px] py-[3px] hover:border-casevo-mid focus:outline-none casevo-focus'
const LABEL = 'text-[10.5px] font-semibold tracking-[0.16em] uppercase text-casevo-text-soft'
const PhoneIcon: React.FC<{ small?: boolean }> = ({ small }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={small ? 'w-3 h-3' : 'w-[14px] h-[14px]'} aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
)

export default AddActivityModal
